import { Component, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChartConfiguration, ChartDataset } from 'chart.js';
import { SalesInsightsKpiResponse, SalesInsightsMeasure } from '../../models/sales-insights.model';
import {
  SalesInsightsDelta,
  formatSalesInsightsAmount,
  formatSalesInsightsPercent,
  salesInsightsDelta
} from '../../utils/sales-insights-format';
import { salesInsightsMonthYear } from '../../utils/sales-insights-labels';
import { salesInsightsRgba, salesInsightsThemeColors } from '../../utils/sales-insights-theme';
import { SalesInsightsCardAccent } from '../sales-insights-card/sales-insights-card.component';
import { SalesInsightsBaseComponent } from '../sales-insights-base/sales-insights-base.component';

type TrendMeasure = 'valueBase' | 'valueLocal' | 'quantity';

const TREND_MEASURES: SalesInsightsMeasure[] = ['valueBase', 'valueLocal', 'quantity'];
const BASE_CURRENCY_MEASURES: SalesInsightsMeasure[] = ['valueBase', 'averageOrderValue'];

@Component({
  selector: 'lib-sales-insights-kpi',
  standalone: false,
  templateUrl: './sales-insights-kpi.component.html',
  styleUrl: './sales-insights-kpi.component.scss'
})
export class SalesInsightsKpiComponent extends SalesInsightsBaseComponent<SalesInsightsKpiResponse> {
  protected translate = inject(TranslateService);

  value = '';
  unit = '';
  subline = '';
  comparisonTitle = '';
  periodRange = '';
  delta: SalesInsightsDelta | null = null;
  targetAttainment = '';
  targetValue = '';
  targetPercent: number | null = null;
  sparklineData: ChartDataset<'line'>[] = [];
  sparklineLabels: string[] = [];
  readonly sparklineOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    events: [],
    layout: { padding: { top: 2 } },
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: { x: { display: false }, y: { display: false } },
    elements: { line: { tension: 0.35, borderWidth: 2 }, point: { radius: 0 } }
  };

  get measure(): SalesInsightsMeasure {
    return this.options.measure || 'valueBase';
  }

  get accent(): SalesInsightsCardAccent {
    return this.options.accent === 'danger' ? 'danger' : 'primary';
  }

  protected isEmpty(data: SalesInsightsKpiResponse | null): boolean {
    return !data?.current || data.current[this.measure] === null || data.current[this.measure] === undefined;
  }

  protected onData(data: SalesInsightsKpiResponse | null): void {
    const current = data?.current?.[this.measure];
    const previous = data?.comparison?.[this.measure];
    const comparisonComplete = !!data?.comparison && data.comparisonComplete !== false;

    this.value = formatSalesInsightsAmount(current);
    this.unit = this.unitFor(data);
    this.delta = comparisonComplete ? salesInsightsDelta(current, previous) : null;
    this.subline = this.sublineFor(data, comparisonComplete);
    this.comparisonTitle = comparisonComplete
      ? `${this.translate.instant('SALES_INSIGHTS_COMPARED_TO')} ${formatSalesInsightsAmount(previous)} ${this.unit}`.trim()
      : '';
    this.periodRange = data?.current?.from && data?.current?.to ? `${data.current.from} → ${data.current.to}` : '';
    this.buildTarget(data);
    if (this.options?.headline === 'attainment' && this.targetPercent !== null) {
      this.subline = `${this.value} ${this.unit}`.trim();
      this.value = formatSalesInsightsPercent(this.targetPercent);
      this.unit = '';
      this.targetAttainment = '';
    }
    this.buildSparkline(data);
  }

  private buildTarget(data: SalesInsightsKpiResponse | null): void {
    const target = this.measure === 'valueBase' ? data?.target : null;
    const attainment = target ? target.attainment : null;
    this.targetPercent = attainment !== null && attainment !== undefined ? attainment * 100 : null;
    this.targetAttainment =
      this.targetPercent !== null
        ? this.translate.instant('SALES_INSIGHTS_KPI_OF_BUDGET_TO_DATE', { percent: formatSalesInsightsPercent(this.targetPercent) })
        : '';
    this.targetValue = target
      ? this.translate.instant('SALES_INSIGHTS_KPI_BUDGET', {
          amount: `${formatSalesInsightsAmount(target.value)} ${this.translate.instant('SALES_INSIGHTS_UNIT_BASE_CURRENCY')}`
        })
      : '';
  }

  private unitFor(data: SalesInsightsKpiResponse | null): string {
    if (BASE_CURRENCY_MEASURES.includes(this.measure)) {
      return this.translate.instant('SALES_INSIGHTS_UNIT_BASE_CURRENCY');
    }
    if (this.measure === 'valueLocal') {
      return data?.current?.currency || '';
    }
    return '';
  }

  private sublineFor(data: SalesInsightsKpiResponse | null, comparisonComplete: boolean): string {
    if (!data?.comparison) {
      return data?.period ? this.translate.instant(`SALES_INSIGHTS_PERIOD_${data.period.toUpperCase()}`) : '';
    }
    if (comparisonComplete) {
      const year = data.comparison.from?.substring(0, 4);
      return year
        ? this.translate.instant('SALES_INSIGHTS_VS_SAME_PERIOD', { year })
        : this.translate.instant('SALES_INSIGHTS_VS_PREVIOUS_PERIOD');
    }
    return data.dataFrom
      ? this.translate.instant('SALES_INSIGHTS_NO_COMPARISON_DATA_FROM', {
          month: salesInsightsMonthYear(this.translate, data.dataFrom)
        })
      : this.translate.instant('SALES_INSIGHTS_NO_COMPARISON');
  }

  private buildSparkline(data: SalesInsightsKpiResponse | null): void {
    const trend = data?.trend || [];
    if (!TREND_MEASURES.includes(this.measure) || !trend.some(point => !!point[this.measure as TrendMeasure])) {
      this.sparklineData = [];
      this.sparklineLabels = [];
      return;
    }
    const colour =
      this.accent === 'danger' ? salesInsightsThemeColors().dangerRgb : this.colorRgb;
    this.sparklineLabels = trend.map(point => point.key);
    this.sparklineData = [
      {
        data: trend.map(point => point[this.measure as TrendMeasure] ?? 0),
        borderColor: salesInsightsRgba(colour, 0.9),
        backgroundColor: salesInsightsRgba(colour, 0.1),
        fill: 'start',
        pointRadius: 0
      }
    ];
  }
}
